var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useTranslation } from "react-i18next";
import { H6 } from "@animech/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import * as Styled from "./application-speed-box.style";
import { SimpleBox } from "ui/components/panels/dashboard/boxes/simple-box";
import { Chart, chartSvgHeight } from "ui/components/elements/chart/chart";
import { ArrowUpLeftIcon } from "ui/components/elements/icons/arrow-up-left";
import { GraphIcon } from "ui/components/elements/icons/graph";
import { IconButton } from "ui/components/elements/icons/icon-button";
import { MenuItem } from "store/types";
import { round } from "utility/number";
import { useAppSelector, selectors, useAppDispatch, actions } from "store";
import { useSystemPerformanceValidityMessage } from "hooks/messages";
import { MessagesPopover, MessageLevel, } from "ui/components/elements/messages-popover";
import { InfoButton } from "ui/components/elements/info-button";
import * as StyledShared from "ui/components/panels/dashboard/boxes/shared.style";
import { EditParameterTooltip } from "ui/components/panels/canvas/overlay-elements/tooltip-components/edit-parameter-tooltip";
import { systemDataConstants } from "api/system-data";
import { EditFieldTooltip } from "ui/components/panels/canvas/overlay-elements/tooltip-components/edit-field-tooltip";
import { EditSecondsPerCycle } from "ui/components/panels/dashboard/boxes/application-speed/edit-sec-per-cycle";
import { chartDrawingAreaStart, chartDrawingAreaWidth, hasToggledChartStorageKey, } from "ui/components/panels/graphs/constants";
import { useChartElementPositions, visualPixelToTimePercent, timePercentToVisualPixel, getPlayLineSnapPercents, } from "ui/components/elements/chart/use-chart-element-positions";
import { PlayLine } from "ui/components/panels/graphs/common/play-simulation/play-line";
import { useGraphDataContext } from "providers/graph-panel";
import { ZoomInIcon } from "ui/components/elements/icons/zoom-in";
import { ZoomOutIcon } from "ui/components/elements/icons/zoom-out";
import { ZoomBox } from "ui/components/panels/dashboard/boxes/application-speed/zoom-box";
import { computePsEsZoomTimes, getPresetZoomForDraggedRange, } from "ui/components/panels/dashboard/boxes/application-speed/application-speed-zoom";
import { getOwnerWindow, getRenderedScale } from "utility/dom";
import { useApplicationSpeedLabels } from "hooks/use-application-speed-labels";
// While dragging the play head, snap it to a cycle key point (PS, ATM,
// energy-save start/end, …) when the cursor lands within this many pixels of
// that point, so the play time matches the event exactly instead of a value
// just off it. Snapping is done in visual-pixel space because the cascade
// layout makes time distances non-linear on screen.
var PLAY_LINE_SNAP_PX = 3;
export var ApplicationSpeedBox = function () {
    var _a, _b;
    var _c = useGraphDataContext(), zoom = _c.zoom, zoomOut = _c.zoomOut, zoomedTimeDomain = _c.zoomedTimeDomain;
    var chartElementPositionsData = useChartElementPositions();
    var _d = __read(useState(0), 2), zoomStartFraction = _d[0], setZoomStartFraction = _d[1];
    var _e = __read(useState(0), 2), zoomEndFraction = _e[0], setZoomEndFraction = _e[1];
    var _f = __read(useState(false), 2), isPlaying = _f[0], setIsPlaying = _f[1]; // we use one state variable for updating the ui and one ref to simplifying starting/stopping of the animation
    var isPlayingRef = useRef(false);
    var _g = __read(useState(false), 2), slowMotion = _g[0], setSlowMotion = _g[1];
    var slowMotionRef = useRef(false);
    var containerRef = useRef(null);
    var requestAnimationFrameRef = useRef(null);
    // The dashboard can be popped out into another window, and only that
    // window's animation frames keep running while it is the one on screen.
    var animationWindowRef = useRef(window);
    var startTimeRef = useRef(null);
    var basePlayLocationRef = useRef(0);
    var playLocationPercent = useAppSelector(selectors.selectPlayLocationPercent);
    var playCompleted = playLocationPercent >= 1;
    var activeMenuItem = useAppSelector(selectors.selectActiveMenuItem);
    var isGraphPanelOpen = useAppSelector(selectors.selectIsGraphPanelOpen);
    var multipleBoxesEnabled = useAppSelector(selectors.selectMultipleBoxesEnabled);
    var isSystemPerformanceLoading = useAppSelector(selectors.selectIsPerformanceLoading);
    var dashboardData = useAppSelector(selectors.selectDashboardData);
    var cupsSettledTimes = useAppSelector(selectors.selectCupsSettledTimes);
    var isSystemDataLoading = useAppSelector(selectors.selectIsSystemDataLoading);
    var isPerformanceDataValid = useAppSelector(selectors.selectIsPerformanceDataValid);
    var hasPPMValue = (dashboardData === null || dashboardData === void 0 ? void 0 : dashboardData.targetPartsPerMinute.value) !== undefined;
    var perMinuteTranslationKey = useApplicationSpeedLabels().perMinuteTranslationKey;
    var paddedTotalTime = (_a = chartElementPositionsData === null || chartElementPositionsData === void 0 ? void 0 : chartElementPositionsData.timeline.totalTime) !== null && _a !== void 0 ? _a : 1;
    // The play head "home" position: the moment PS (part secured) is reached.
    // The build-up before this point (vacuum-on / blow-off / time to part secured)
    // is not what the user wants to replay, so playback starts here and returns
    // here when it completes or is stopped.
    var startPlayLocationPercent = chartElementPositionsData
        ? chartElementPositionsData.timeline.timeToPartSecured.end /
            chartElementPositionsData.timeline.totalTime
        : 0;
    var dispatch = useAppDispatch();
    var t = useTranslation().t;
    var systemPerformanceMessage = useSystemPerformanceValidityMessage();
    // Pulse the chart and application speed buttons until the user has opened
    // or closed the charts themselves. The charts fading out by themselves
    // after the first calculation does not count (see `GraphPanel`).
    var userHasToggledChart = Boolean(sessionStorage.getItem(hasToggledChartStorageKey));
    var toggleApplicationSpeedMenu = useCallback(function () {
        dispatch(actions.openMenuItem(activeMenuItem !== MenuItem.APPLICATION_SPEED
            ? MenuItem.APPLICATION_SPEED
            : null));
    }, [activeMenuItem, dispatch]);
    var pausePlaying = useCallback(function () {
        if (requestAnimationFrameRef.current) {
            animationWindowRef.current.cancelAnimationFrame(requestAnimationFrameRef.current);
        }
        isPlayingRef.current = false;
        setIsPlaying(false);
        startTimeRef.current = null;
        requestAnimationFrameRef.current = null;
    }, []);
    var stopPlaying = useCallback(function () {
        pausePlaying();
        dispatch(actions.setPlayLocationPercent(startPlayLocationPercent));
    }, [dispatch, pausePlaying, startPlayLocationPercent]);
    var svgWidth = (_b = chartElementPositionsData === null || chartElementPositionsData === void 0 ? void 0 : chartElementPositionsData.positions.returnTime.x2) !== null && _b !== void 0 ? _b : chartDrawingAreaWidth;
    var applyZoomByTimes = useCallback(function (startTime, endTime) {
        if (!chartElementPositionsData)
            return;
        var timeline = chartElementPositionsData.timeline, timePixelMap = chartElementPositionsData.timePixelMap, percentToDisplayTime = chartElementPositionsData.percentToDisplayTime;
        var clampedStart = Math.max(0, startTime);
        var clampedEnd = Math.min(timeline.totalTime, endTime);
        var startFraction = timePercentToVisualPixel(clampedStart / timeline.totalTime, timePixelMap) / svgWidth;
        var endFraction = timePercentToVisualPixel(clampedEnd / timeline.totalTime, timePixelMap) / svgWidth;
        setZoomStartFraction(Math.max(0, startFraction));
        setZoomEndFraction(Math.min(1, endFraction));
        zoom(percentToDisplayTime(clampedStart / timeline.totalTime), percentToDisplayTime(clampedEnd / timeline.totalTime));
    }, [chartElementPositionsData, svgWidth, zoom]);
    var handleZoomChange = useCallback(function (start, end, dragType) {
        if (!chartElementPositionsData) {
            setZoomStartFraction(start);
            setZoomEndFraction(end);
            zoom(start, end);
            return;
        }
        var timeline = chartElementPositionsData.timeline, timePixelMap = chartElementPositionsData.timePixelMap;
        var fractionToTimeSeconds = function (f) {
            var pct = visualPixelToTimePercent(f * svgWidth, timePixelMap);
            return pct * timeline.totalTime;
        };
        var prevStartTime = fractionToTimeSeconds(zoomStartFraction);
        var prevEndTime = fractionToTimeSeconds(zoomEndFraction);
        var newStartTime = fractionToTimeSeconds(start);
        var newEndTime = fractionToTimeSeconds(end);
        var presetZoomRange = getPresetZoomForDraggedRange({
            timeline: timeline,
            cupsSettledTimes: cupsSettledTimes,
            previousRange: {
                startTime: prevStartTime,
                endTime: prevEndTime,
            },
            newRange: {
                startTime: newStartTime,
                endTime: newEndTime,
            },
            dragType: dragType,
        });
        if (presetZoomRange) {
            applyZoomByTimes(presetZoomRange.startTime, presetZoomRange.endTime);
            return;
        }
        setZoomStartFraction(start);
        setZoomEndFraction(end);
        var startTimePercent = visualPixelToTimePercent(start * svgWidth, timePixelMap);
        var endTimePercent = visualPixelToTimePercent(end * svgWidth, timePixelMap);
        zoom(chartElementPositionsData.percentToDisplayTime(startTimePercent), chartElementPositionsData.percentToDisplayTime(endTimePercent));
    }, [
        zoom,
        chartElementPositionsData,
        svgWidth,
        zoomStartFraction,
        zoomEndFraction,
        applyZoomByTimes,
        cupsSettledTimes,
    ]);
    var animate = useCallback(function (timestamp) {
        if (!startTimeRef.current) {
            startTimeRef.current = timestamp;
        }
        var elapsed = timestamp - startTimeRef.current;
        // paddedTotalTime is in seconds; elapsed is in milliseconds, so divide by 1000.
        // slowMotionRef scales duration by 10x.
        var slowFactor = slowMotionRef.current ? 10 : 1;
        var progress = Math.min(elapsed / (paddedTotalTime * 1000 * slowFactor), 1);
        dispatch(actions.setPlayLocationPercent((basePlayLocationRef.current || 0) + progress));
        if (progress < 1 && isPlayingRef.current) {
            requestAnimationFrameRef.current =
                animationWindowRef.current.requestAnimationFrame(animate);
        }
        else {
            stopPlaying();
        }
    }, [dispatch, stopPlaying, paddedTotalTime]);
    var startPlaying = useCallback(function () {
        if (requestAnimationFrameRef.current !== null)
            return;
        setIsPlaying(true);
        basePlayLocationRef.current = playLocationPercent; // start at the last pause position or at the beginning
        startTimeRef.current = null;
        isPlayingRef.current = true;
        animationWindowRef.current = getOwnerWindow(containerRef.current);
        requestAnimationFrameRef.current =
            animationWindowRef.current.requestAnimationFrame(animate);
    }, [animate, playLocationPercent]);
    var toggleSlowMotion = useCallback(function () {
        var newValue = !slowMotionRef.current;
        slowMotionRef.current = newValue;
        setSlowMotion(newValue);
        if (isPlayingRef.current) {
            // Reset timing so speed change takes effect from current position
            basePlayLocationRef.current = playLocationPercent;
            startTimeRef.current = null;
        }
    }, [playLocationPercent]);
    // stop animation when completed or when graph window is closed
    useEffect(function () {
        if ((playCompleted && !isDraggingRef.current) || !isGraphPanelOpen) {
            zoomOut();
            stopPlaying();
        }
    }, [playCompleted, stopPlaying, isGraphPanelOpen, zoomOut]);
    // cancel animation on unmount
    useEffect(function () {
        return function () {
            if (requestAnimationFrameRef.current) {
                animationWindowRef.current.cancelAnimationFrame(requestAnimationFrameRef.current);
            }
        };
    }, []);
    var isDraggingRef = useRef(false);
    // Position the play head at the PS-reached point when the panel opens or new
    // performance data arrives, instead of at the very start of the timeline.
    // Skipped while the user is actively playing or dragging the play head.
    useEffect(function () {
        if (!isGraphPanelOpen || !chartElementPositionsData)
            return;
        if (isPlayingRef.current || isDraggingRef.current)
            return;
        dispatch(actions.setPlayLocationPercent(startPlayLocationPercent));
    }, [
        isGraphPanelOpen,
        chartElementPositionsData,
        startPlayLocationPercent,
        dispatch,
    ]);
    var handlePlayLineDrag = useCallback(function (e) {
        e.preventDefault();
        isDraggingRef.current = true;
        var container = containerRef.current;
        if (!container)
            return;
        var containerRect = container.getBoundingClientRect();
        // The dashboard is shrunk with `transform: scale()` on small viewports
        // (see useDashboardScale). Mouse coordinates are screen pixels while the
        // chart is laid out in unscaled pixels, so screen distances measured from
        // the container's edge must be divided by the applied scale.
        var renderedScale = getRenderedScale(container);
        var ownerWindow = getOwnerWindow(container);
        var rafId = null;
        var latestClientX = 0;
        var lastPercent = 0;
        // Precompute the on-screen pixel of every cycle key point once per drag
        // so each mouse move only does cheap distance comparisons. ATM snaps to
        // the data-derived cup-release point (see getPlayLineSnapPercents).
        var snapTargets = chartElementPositionsData
            ? getPlayLineSnapPercents(chartElementPositionsData).map(function (percent) { return ({
                percent: percent,
                pixel: timePercentToVisualPixel(percent, chartElementPositionsData.timePixelMap),
            }); })
            : [];
        var onMouseMove = function (moveEvent) {
            latestClientX = moveEvent.clientX;
            if (rafId !== null)
                return;
            rafId = ownerWindow.requestAnimationFrame(function () {
                rafId = null;
                var visualPixel = Math.max(0, Math.min((latestClientX - containerRect.left) / renderedScale -
                    chartDrawingAreaStart, svgWidth));
                var snapTarget = snapTargets.find(function (target) {
                    return Math.abs(target.pixel - visualPixel) <= PLAY_LINE_SNAP_PX;
                });
                var percent = Math.min(snapTarget
                    ? snapTarget.percent
                    : chartElementPositionsData
                        ? visualPixelToTimePercent(visualPixel, chartElementPositionsData.timePixelMap)
                        : visualPixel / svgWidth, 0.99);
                lastPercent = percent;
                dispatch(actions.setPlayLocationPercent(percent));
                if (isPlayingRef.current) {
                    basePlayLocationRef.current = percent;
                    startTimeRef.current = null;
                }
            });
        };
        var onMouseUp = function () {
            if (rafId !== null) {
                ownerWindow.cancelAnimationFrame(rafId);
                rafId = null;
            }
            isDraggingRef.current = false;
            ownerWindow.document.removeEventListener("mousemove", onMouseMove);
            ownerWindow.document.removeEventListener("mouseup", onMouseUp);
            if (lastPercent >= 1) {
                stopPlaying();
            }
        };
        ownerWindow.document.addEventListener("mousemove", onMouseMove);
        ownerWindow.document.addEventListener("mouseup", onMouseUp);
    }, [svgWidth, chartElementPositionsData, dispatch, stopPlaying]);
    // Reserved space at the bottom of SimpleBox for the parts/min + sec/cycle labels
    var playLineBottomOffset = chartSvgHeight - 35;
    return (_jsx(Styled.ApplicationSpeedBox, { ref: containerRef, children: _jsxs(SimpleBox, { isLoading: isSystemDataLoading || isSystemPerformanceLoading, children: [_jsxs(Styled.Columns, { children: [_jsxs(Styled.LeftColumn, { children: [dashboardData && (_jsxs(Styled.PulseAnimation, { onMouseDown: function (event) { return event.stopPropagation(); }, active: isPerformanceDataValid && !userHasToggledChart, children: [_jsx(IconButton, { color: "secondary", appearance: "inverted", onClick: toggleApplicationSpeedMenu, tooltip: t("dashboard.application_speed_tooltip"), children: _jsx(ArrowUpLeftIcon, { size: 18 }) }), _jsx(IconButton, { color: "secondary", appearance: "inverted", onClick: function () {
                                                return dispatch(actions.toggleGraphPanel(!isGraphPanelOpen));
                                            }, tooltip: t("graph_panel.toggle"), rootAttributes: { "aria-label": t("graph_panel.toggle") }, children: _jsx(GraphIcon, { size: 18 }) }), isGraphPanelOpen && (_jsx(IconButton, { color: "secondary", appearance: "inverted", onClick: function () {
                                                if (zoomedTimeDomain) {
                                                    zoomOut();
                                                }
                                                else if (chartElementPositionsData) {
                                                    var times = computePsEsZoomTimes(chartElementPositionsData.timeline, cupsSettledTimes);
                                                    applyZoomByTimes(times.startTime, times.endTime);
                                                }
                                            }, tooltip: t(zoomedTimeDomain
                                                ? "graph_panel.zoom_out"
                                                : "graph_panel.zoom_in"), children: zoomedTimeDomain ? (_jsx(ZoomOutIcon, { size: 18 })) : (_jsx(ZoomInIcon, { size: 18 })) }))] })), _jsx(Styled.PlayControl, { align: "left", visible: Boolean(dashboardData && isGraphPanelOpen), children: _jsx("button", { disabled: Math.abs(playLocationPercent - startPlayLocationPercent) <
                                            0.0001, onClick: stopPlaying, children: _jsx("img", { src: "img/app/stop-icon.svg" }) }) })] }), _jsxs(Styled.MainColumn, { children: [_jsxs(StyledShared.TitleContainer, { children: [_jsx(H6, { children: isPerformanceDataValid
                                                ? "\u00A0"
                                                : t("dashboard.application_speed") }), systemPerformanceMessage && (_jsx(MessagesPopover, { messages: [systemPerformanceMessage], level: MessageLevel.WARNING, toggleEvent: "hover", size: "small" }))] }), _jsxs(Styled.ChartContainer, { children: [_jsx(Chart, {}), isGraphPanelOpen &&
                                            zoomedTimeDomain &&
                                            chartElementPositionsData && (_jsx(ZoomBox, { startFraction: zoomStartFraction, endFraction: zoomEndFraction, totalWidth: svgWidth, toDisplayTime: function (fraction) {
                                                return chartElementPositionsData.percentToDisplayTime(visualPixelToTimePercent(fraction * svgWidth, chartElementPositionsData.timePixelMap));
                                            }, onChange: handleZoomChange }))] })] }), _jsxs(Styled.RightColumn, { children: [_jsx(InfoButton, { title: t("dashboard.application_speed"), description: {
                                        long: t(multipleBoxesEnabled
                                            ? "descriptions.dashboard.application_speed_multi_box"
                                            : "descriptions.dashboard.application_speed"),
                                    } }), _jsxs(Styled.PlayControlGroup, { children: [_jsx(Styled.PlayControl, { align: "right", visible: Boolean(dashboardData && isGraphPanelOpen), children: _jsx("button", { onClick: isPlaying ? pausePlaying : startPlaying, children: isPlaying ? (_jsx("img", { src: "img/app/paus-icon.svg" })) : (_jsx("img", { src: "img/app/play-icon.svg" })) }) }), dashboardData && isGraphPanelOpen && (_jsxs(Styled.SlowMotionLabel, { children: [_jsx("input", { type: "checkbox", checked: slowMotion, onChange: toggleSlowMotion }), "0.1\u00D7"] }))] })] })] }), _jsxs(Styled.LeftSideBoxes, { children: [_jsxs(Styled.LabelValuePair, { children: [hasPPMValue ? (_jsx(EditParameterTooltip, { parameterNames: [
                                        systemDataConstants.parameterNames.targetPartsPerMinute,
                                    ], anchorElement: _jsx(Styled.ClickableText, { children: round(dashboardData === null || dashboardData === void 0 ? void 0 : dashboardData.targetPartsPerMinute.value, 3) }) })) : (_jsx("span", { children: "-" })), _jsx("span", { children: t(perMinuteTranslationKey) })] }), _jsxs(Styled.LabelValuePair, { children: [(dashboardData === null || dashboardData === void 0 ? void 0 : dashboardData.targetSecsPerCycle.value) ? (_jsx(EditFieldTooltip, { anchorElement: _jsx(Styled.ClickableText, { children: round(dashboardData === null || dashboardData === void 0 ? void 0 : dashboardData.targetSecsPerCycle.value, 3) }), children: _jsx(EditSecondsPerCycle, {}) })) : (_jsx("span", { children: "-" })), _jsx("span", { children: t("dashboard.sec_per_cycle") })] })] }), dashboardData && isGraphPanelOpen && (_jsx(PlayLine, { playLocationPercent: chartElementPositionsData
                        ? timePercentToVisualPixel(playLocationPercent, chartElementPositionsData.timePixelMap) / chartDrawingAreaWidth
                        : playLocationPercent, label: "".concat(round(chartElementPositionsData
                        ? chartElementPositionsData.percentToDisplayTime(playLocationPercent)
                        : playLocationPercent, 2), "s"), bottomOffset: playLineBottomOffset, onHandleMouseDown: handlePlayLineDrag }))] }) }));
};
